import { useState, useEffect, useRef } from 'react'
import {
  ListIcon, BoardIcon, CalendarIcon, GanttIcon, TableIcon, ChartIcon,
  SearchIcon, FilterIcon, PlusIcon, BotIcon, ChevronDownIcon, UserIcon,
  ChevronRightIcon, SettingsIcon, XIcon
} from '../icons'
import { avatarUrl } from '../utils/avatar'
import type { PublicUser } from '../types'
import { API_URL, getAuthHeaders } from '../api'


const views = [
  { id: 'board', label: 'Board', icon: <BoardIcon size={16} /> },
  { id: 'list', label: 'List', icon: <ListIcon size={16} /> },
  { id: 'calendar', label: 'Calendar', icon: <CalendarIcon size={16} /> },
  { id: 'gantt', label: 'Gantt', icon: <GanttIcon size={16} /> },
  { id: 'table', label: 'Table', icon: <TableIcon size={16} /> },
  { id: 'chart', label: '+ Visualização', icon: <ChartIcon size={16} /> },
]

export function TopBar({ title, subtitle, currentView, onChangeView, onCreateTask, onToggleAgent, agentOpen, searchQuery, onSearchChange, assigneeFilter, onAssigneeFilterChange }: { title?: string; subtitle?: string; currentView?: string; onChangeView?: (v: string) => void; onCreateTask?: () => void; onToggleAgent?: () => void; agentOpen?: boolean; searchQuery?: string; onSearchChange?: (v: string) => void; assigneeFilter?: string; onAssigneeFilterChange?: (v: string) => void }) {
  const [users, setUsers] = useState<PublicUser[]>([])
  const [assigneeOpen, setAssigneeOpen] = useState(false)
  const assigneeRef = useRef<HTMLDivElement | null>(null)

  const authHeaders = getAuthHeaders()

  useEffect(() => {
    fetch(`${API_URL}/api/users`, { headers: authHeaders })
      .then(r => r.json())
      .then(data => {
        const list = data.users || (Array.isArray(data) ? data : [])
        setUsers(list)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target as Node)) {
        setAssigneeOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const assigneeLabel = (u: PublicUser) => u.display_name || u.name || u.email

  return (
    <header className="top-bar">
      <div className="top-bar-left">
        <h1 className="page-title">{title}</h1>
        {subtitle && (
          <span className="page-subtitle">{subtitle}</span>
        )}
        <div className="view-tabs">
          {views.map(v => (
            <button
              key={v.id}
              className={`view-tab ${currentView === v.id ? 'active' : ''}`}
              onClick={() => onChangeView?.(v.id)}
            >
              {v.icon}
              <span>{v.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="top-bar-right">
        <div className="search-box">
          <SearchIcon size={16} />
          <input
            type="text"
            placeholder="Buscar tarefas..."
            value={searchQuery || ''}
            onChange={e => onSearchChange?.(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => onSearchChange?.('')}>
              <XIcon size={14} />
            </button>
          )}
        </div>
        <div className="assignee-filter-wrapper" ref={assigneeRef}>
          <button className={`btn-ghost ${assigneeFilter ? 'active' : ''}`} onClick={() => setAssigneeOpen(!assigneeOpen)}>
            <UserIcon size={14} />
            {assigneeFilter || 'Responsável'}
            <ChevronDownIcon size={12} />
          </button>
          {assigneeOpen && (
            <div className="assignee-filter-dropdown">
              <button
                className={`assignee-filter-option ${!assigneeFilter ? 'selected' : ''}`}
                onClick={() => { onAssigneeFilterChange?.(''); setAssigneeOpen(false) }}
              >
                <span className="assignee-avatar-fallback"><UserIcon size={14} /></span>
                <span>Todos</span>
              </button>
              {users.map(u => {
                const label = assigneeLabel(u)
                const av = avatarUrl(u)
                return (
                  <button
                    key={u.id}
                    className={`assignee-filter-option ${assigneeFilter === label ? 'selected' : ''}`}
                    onClick={() => { onAssigneeFilterChange?.(label); setAssigneeOpen(false) }}
                  >
                    {av ? (
                      <img src={av} alt="" className="assignee-avatar" />
                    ) : (
                      <span className="assignee-avatar-fallback"><UserIcon size={14} /></span>
                    )}
                    <span>{label}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
